<template>
  <div class="compliance-pill-wrap">
    <span
      v-if="!summary || summary.empty"
      class="compliance-pill--empty mx-2"
      :title="failed ? $t('acceptance.list.load_failed') : ''"
    >
      —
    </span>
    <button
      v-else
      type="button"
      class="compliance-pill"
      :aria-label="$t('acceptance.list.compliance_title')"
      @click.stop="open = true"
    >
      <span class="compliance-pill__dot" :class="`bg-${summary.color}`" />
      <span class="compliance-pill__text">
        <span class="compliance-pill__label">
          {{ $t("acceptance.list.compliance") }}
        </span>
        <span v-if="summary.first.length" class="compliance-pill__line">
          {{ firstLine }}
        </span>
        <span
          v-if="summary.second"
          class="compliance-pill__line compliance-pill__line--second"
          :class="`text-${summary.second.color}`"
        >
          {{ chipText(summary.second) }}
        </span>
        <span
          v-if="summary.released"
          class="compliance-pill__line compliance-pill__line--second text-grey-darken-1"
        >
          {{ chipText(summary.released) }}
        </span>
      </span>
      <v-icon size="18" class="compliance-pill__chevron">
        mdi-chevron-right
      </v-icon>
    </button>

    <v-bottom-sheet v-if="summary" v-model="open" scrollable>
      <v-card>
        <v-card-title class="text-wrap pb-0">
          {{ $t("acceptance.list.compliance_title") }}
        </v-card-title>
        <div class="px-4 pt-1 pb-2">
          <div class="text-primary font-weight-bold">{{ shortId }}</div>
          <div class="text-body-2">{{ vehicleName }}</div>
          <div v-if="car.pra" class="text-caption">
            {{ $t("vehicle_entry.datatable.practice_number") }}:
            <strong>{{ car.pra }}</strong>
          </div>
        </div>
        <v-card-text class="pt-0">
          <v-sheet
            v-for="section in summary.sections"
            :key="section.id"
            border
            rounded
            class="pa-3 mb-2"
          >
            <div class="text-subtitle-2 mb-1">{{ $t(section.titleKey) }}</div>
            <div class="d-flex flex-wrap align-center ga-1">
              <v-chip
                v-for="chip in section.chips"
                :key="chip.key"
                size="small"
                variant="tonal"
                :color="chip.color"
                :title="chipTitle(chip)"
              >
                {{ chipText(chip) }}
              </v-chip>
              <span v-if="!section.chips.length">—</span>
            </div>
            <div v-if="section.deadlines" class="text-caption mt-1">
              {{ $t("acceptance.list.depollution_short") }}
              {{ section.deadlines.depollution }} ·
              {{ $t("acceptance.list.deregistration_short") }}
              {{ section.deadlines.deregistration }}
            </div>
            <div v-if="section.dueDate" class="text-caption mt-1">
              {{
                $t("acceptance.depollution.list.due_on", {
                  date: section.dueDate,
                })
              }}
            </div>
          </v-sheet>
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-btn block variant="tonal" @click="open = false">
            {{ $t("acceptance.list.compliance_close") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-bottom-sheet>
  </div>
</template>

<script>
// Mobile vehicle cards: a "Compliance" pill (dot, short states, chevron) that opens a bottom sheet
// with one labelled section per value (acceptance, PRA case, depollution). summary comes from
// complianceSummary.mjs; null while the by-cars data loads or after it failed (shown as "—").
export default {
  props: {
    car: { type: Object, required: true },
    summary: { type: Object, default: null },
    failed: { type: Boolean, default: false },
  },
  data: () => ({
    open: false,
  }),
  computed: {
    firstLine() {
      return this.summary.first.map((chip) => this.chipText(chip)).join(" · ");
    },
    shortId() {
      const id = String(this.car._id || "");
      return id.slice(-6);
    },
    vehicleName() {
      const brand = this.car.brand && this.car.brand.title;
      const model = this.car.model && this.car.model.description;
      return [brand, model].filter(Boolean).join(", ");
    },
  },
  methods: {
    chipText(chip) {
      return this.$t(chip.key, chip.params || {});
    },
    chipTitle(chip) {
      const title = chip.title;
      return title ? this.$t(title.key, title.params) : undefined;
    },
  },
};
</script>

<style scoped>
.compliance-pill-wrap {
  flex: 1 1 160px;
  min-width: 0;
  max-width: 100%;
}
.compliance-pill {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 4px 6px 4px 8px;
  margin: 2px 0;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  text-align: left;
}
.compliance-pill__dot {
  flex: 0 0 auto;
  width: 8px;
  height: 8px;
  border-radius: 50%;
}
.compliance-pill__text {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-width: 0;
  line-height: 1.25;
}
.compliance-pill__label {
  font-size: 0.6875rem;
  color: rgba(0, 0, 0, 0.6);
}
.compliance-pill__line {
  overflow: hidden;
  font-size: 0.8125rem;
  font-weight: 500;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.compliance-pill__line--second {
  font-size: 0.75rem;
}
.compliance-pill__chevron {
  flex: 0 0 auto;
}
</style>
